import React, { useState, useEffect } from 'react';
import { 
  ClipboardDocumentListIcon, 
  DocumentTextIcon, 
  BeakerIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../auth/AuthContext';
import api from '../services/api';

export default function ClinicalProtocols() {
  const { user } = useAuth();
  const [protocols, setProtocols] = useState([]);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingProtocol, setUploadingProtocol] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState('all');

  useEffect(() => {
    loadProtocolData();
  }, []);

  const loadProtocolData = async () => {
    try {
      const [protocolsResponse, studiesResponse] = await Promise.all([
        api.get('/health/clinical/protocols'),
        api.get('/health/clinical/studies')
      ]);
      
      setProtocols(protocolsResponse.data);
      setStudies(studiesResponse.data);
    } catch (error) {
      console.error('Failed to load protocol data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProtocolUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingProtocol(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/health/clinical/protocols/upload', formData);
      await loadProtocolData();
      event.target.value = '';
    } catch (error) {
      console.error('Failed to upload protocol:', error);
      alert('Failed to upload protocol. Please try again.');
    } finally {
      setUploadingProtocol(false);
    }
  };

  const studyPhases = [
    { id: 'preclinical', name: 'Preclinical', color: 'gray' },
    { id: 'phase1', name: 'Phase I', color: 'blue' },
    { id: 'phase2', name: 'Phase II', color: 'green' },
    { id: 'phase3', name: 'Phase III', color: 'yellow' },
    { id: 'phase4', name: 'Phase IV', color: 'purple' }
  ];

  const protocolTypes = [
    {
      name: 'Clinical Study Protocol',
      description: 'Main study protocol documents',
      icon: ClipboardDocumentListIcon,
      color: 'blue'
    },
    {
      name: 'Informed Consent Forms',
      description: 'Patient consent documentation',
      icon: DocumentTextIcon,
      color: 'green'
    },
    {
      name: 'Investigator Brochures',
      description: 'Study drug/device information',
      icon: BeakerIcon,
      color: 'purple'
    },
    {
      name: 'Case Report Forms',
      description: 'Data collection templates',
      icon: UserGroupIcon,
      color: 'red'
    }
  ];

  const filteredStudies = selectedPhase === 'all' 
    ? studies 
    : studies.filter(study => study.phase === selectedPhase);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Clinical Protocols</h1>
        <p className="mt-2 text-gray-600">
          Manage clinical study protocols and FDA regulatory documentation
        </p>
      </div>

      {/* Protocol Upload Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Upload Clinical Protocol</h2>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
            <PlusIcon className="h-4 w-4 mr-2" />
            New Study
          </button>
        </div>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <div className="text-center">
            <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <label className="cursor-pointer">
                <span className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                  {uploadingProtocol ? 'Uploading...' : 'Choose File'}
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx"
                  onChange={handleProtocolUpload}
                  disabled={uploadingProtocol}
                />
              </label>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Protocol documents (PDF, DOC, DOCX up to 50MB)
            </p>
          </div>
        </div>
      </div>

      {/* Protocol Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {protocolTypes.map((type) => {
          const Icon = type.icon;
          const typeCount = protocols.filter(p => p.type === type.name).length;
          
          return (
            <div key={type.name} className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <Icon className={`h-8 w-8 text-${type.color}-500`} />
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${type.color}-100 text-${type.color}-800`}>
                  {typeCount}
                </span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{type.name}</h3>
              <p className="text-sm text-gray-500">{type.description}</p>
            </div>
          );
        })}
      </div>

      {/* Study Phase Filter */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Clinical Studies by Phase</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setSelectedPhase('all')}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              selectedPhase === 'all'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Phases
          </button>
          {studyPhases.map((phase) => (
            <button
              key={phase.id}
              onClick={() => setSelectedPhase(phase.id)}
              className={`px-3 py-2 rounded-md text-sm font-medium ${
                selectedPhase === phase.id
                  ? `bg-${phase.color}-100 text-${phase.color}-800`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {phase.name}
            </button>
          ))}
        </div>
        
        {/* Studies List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStudies.map((study) => {
            const phase = studyPhases.find(p => p.id === study.phase) || studyPhases[0];
            return (
              <div key={study.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-medium text-gray-900">{study.title}</h4>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${phase.color}-100 text-${phase.color}-800`}>
                    {phase.name}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{study.indication}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Protocol: {study.protocol_number}</span>
                  <div className="flex items-center">
                    {study.status === 'active' ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                    ) : (
                      <ClockIcon className="h-4 w-4 text-yellow-500 mr-1" />
                    )}
                    {study.status}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {study.enrollment_current}/{study.enrollment_target} enrolled
                  </span>
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-600 h-2 rounded-full" 
                      style={{width: `${(study.enrollment_current / study.enrollment_target) * 100}%`}}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
          {filteredStudies.length === 0 && (
            <div className="col-span-full text-center py-8 text-gray-500">
              No studies found for selected phase
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Protocols */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Protocol Updates</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {protocols.slice(0, 5).map((protocol) => (
              <div key={protocol.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{protocol.title}</h4>
                    <p className="text-sm text-gray-500">Version {protocol.version}</p>
                    <div className="flex items-center mt-1 text-xs text-gray-400">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {new Date(protocol.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center">
                    {protocol.status === 'approved' ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : protocol.status === 'under_review' ? (
                      <ClockIcon className="h-5 w-5 text-yellow-500" />
                    ) : (
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                </div>
              </div>
            ))}
            {protocols.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500">
                No protocols uploaded yet
              </div>
            )}
          </div>
        </div>

        {/* Regulatory Timeline */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Regulatory Milestones</h3>
          </div>
          <div className="px-6 py-4">
            <div className="flow-root">
              <ul className="-mb-8">
                <li>
                  <div className="relative pb-8">
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                          <CheckCircleIcon className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">IND Application <span className="font-medium text-gray-900">submitted</span></p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          2 days ago
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="relative pb-8">
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-yellow-500 flex items-center justify-center ring-8 ring-white">
                          <ClockIcon className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">FDA Review <span className="font-medium text-gray-900">in progress</span></p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          25 days left
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="relative">
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center ring-8 ring-white">
                          <BeakerIcon className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">Phase I Study <span className="font-medium text-gray-900">pending</span></p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          Scheduled
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}